import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useSpring } from 'framer-motion';
import { Github, ExternalLink, ArrowRight, ChevronRight, BookOpen, Briefcase, X, CheckCircle2, Youtube, Send, MessageSquare } from 'lucide-react';

// --- Mock Data ---
const PROJECTS = [
  {
    id: 'sellermap',
    title: "SellerMap",
    subtitle: "Integrated Landing & Analytics for Individual Sellers",
    description: "A comprehensive platform addressing the fragmentation of product exposure across multiple SNS and commerce platforms for individual sellers.",
    longDescription: "SellerMap helps individual sellers optimize their landing pages and track conversions that are otherwise scattered across various platforms. By providing a customizable multi-link solution with high design freedom, it maximizes user conversion rates and provides a visual dashboard for key metrics.",
    tech: ["React", "TypeScript", "Redux Toolkit", "Zod", "Protobuf", "AWS", "Firebase"],
    image: "/assets/project/sellermap.webp",
    featured: true,
    link: "https://sellermap.co.kr/",
    details: {
      features: [
        "Product & Exhibition management with drag-and-drop placement",
        "Visual dashboard for visits and click-through rates",
        "Plan-based feature exposure control",
        "Template, font, and background customization"
      ],
      solving: [
        "Resolved Instagram In-app browser rendering issues by shifting from Micro Frontends (Module Federation) to a more stable host-integrated architecture.",
        "Eliminated API specification drift by using Git submodules and Protobuf for single-source-of-truth contract management.",
        "Optimized responsive UI using a custom px-to-vw conversion utility with CSS clamp for consistent accessibility across devices."
      ],
      result: "Achieved initial monetization with 3 annual subscriptions (₩78,000/yr) among 60 active users. Business registration completed in July 2025."
    }
  },
  {
    id: 'movble',
    title: "Movble",
    subtitle: "AI-Powered Barrier-Free Travel Guide",
    description: "A travel recommendation service specifically designed for people with disabilities and the mobility impaired.",
    longDescription: "Movble provides detailed barrier-free travel information mapped out for easy navigation. It uses AI to recommend personalized travel itineraries based on user needs, ensuring accessibility is the top priority for every trip.",
    tech: ["React", "Node.js", "Firebase", "Naver Map API", "OpenAI"],
    image: "/assets/project/Movble.webp",
    featured: true,
    link: "https://m.blog.naver.com/PostView.naver?blogId=riblog&logNo=223318891001",
    youtubeLink: "https://youtu.be/M1jUNlsyRG4",
    details: {
      features: [
        "Kakao social login integration",
        "Map-based barrier-free information visualization",
        "AI-driven travel path recommendations",
        "Responsive UI for travelers on the go"
      ],
      solving: [
        "Overcame CORS challenges by building and deploying a dedicated Node.js backend to handle multiple Open API integrations.",
        "Seamlessly combined National Data Portal API with Naver Map API to provide real-time accessibility data."
      ],
      result: "Awarded Grand Prize at the 'Impact Campus IT Service Competition'."
    }
  },
  {
    id: 'pard-web',
    title: "PARD Official Website",
    subtitle: "The Gateway to IT Collaboration",
    description: "Official landing page and recruiting platform for PARD, a university IT collaboration club.",
    longDescription: "Designed to introduce PARD to the public and streamline the recruiting process. The site features a showcase of member projects and a dedicated application portal.",
    tech: ["React", "Firebase", "Framer Motion"],
    image: "/assets/project/pard.png",
    featured: true,
    link: "https://we-pard.com/",
    details: {
      features: [
        "Member project showcase gallery",
        "Interactive recruiting application form",
        "Custom URL deployment via Gabia",
        "Responsive design for all device types"
      ],
      solving: [
        "Led a team of 8 junior React developers using Notion Kanban to manage tasks and maintain a steady development pace.",
        "Fostered a collaborative environment between designers and planners to align on project vision and technical constraints."
      ],
      result: "Successfully launched as the primary hub for PARD activities and recruitment."
    }
  },
  {
    id: 'hgu-student',
    title: "Handong Student Council",
    subtitle: "Campus Communication Hub",
    description: "Official platform for Handong Global University's Student Council to manage feedback and announcements.",
    longDescription: "A comprehensive service providing student council information, feedback channels, and resource archives for students, faculty, and staff. Includes a dedicated admin panel for efficient content management.",
    tech: ["React", "Spring Boot", "AWS EC2", "Firebase"],
    image: "/assets/project/hgu.webp",
    featured: false,
    details: {
      features: [
        "Google Social Login for students and admins",
        "Feedback and inquiry submission system",
        "Real-time notice and resource archive",
        "Full admin dashboard for website management"
      ],
      solving: [
        "Led 5 junior developers through the full SDLC, emphasizing QA and effective communication with student council stakeholders.",
        "Implemented standardized API specifications and shared component libraries to improve development efficiency and code reuse."
      ],
      result: "Deployed via AWS EC2 (User) and Firebase Hosting (Admin), currently serving the university community."
    },
    adminImage: "/assets/project/hgu_admin.webp"
  }
];

const EXPERIENCES = [
  {
    company: "Freelance Developer",
    role: "Full-Stack Engineer",
    period: "05.2025 - Present",
    description: [
      { text: "Jay Partner: Led the full lifecycle of an enterprise admin dashboard—from planning and UI/UX design to full-stack implementation—optimizing data visualization and hierarchical account management." },
      { text: "UpToYou: Engineered a high-conversion responsive landing page and administrative console; managed end-to-end delivery including publishing and cloud deployment.", link: "https://stizen-landingpage.web.app/" },
      { text: "Labor Attorney ERP: Designed and implemented a custom ERP system for labor attorney offices, streamlining workflows with integrated customer, document, and schedule management." },
      { text: "Seoul Coin OTC: Developed a professional landing page for a cryptocurrency OTC exchange, focusing on reliability and secure user experience.", link: "https://www.seoulcoin-otc.com/" },
      { text: "Jeonrado Youth Order Management: Developed an automated procurement and order management tool for the Jeonrado Youth commercial initiative." },
      { text: "FinPilot: Built a mobile application for automated cryptocurrency trading, featuring real-time market data integration and automated execution strategies.", link: "https://play.google.com/store/apps/details?id=com.finpilot.finpilotApp" }
    ]
  },
  {
    company: "HealingPaper - KOS",
    role: "Frontend Developer intern",
    period: "08.2024 - 03.2025",
    link: "https://blog.gangnamunni.com/post/saas-prologue/",
    description: [
      { text: "Developed reliable and testable frontend features using Test-Driven Development (TDD), with structured state and error management via Zustand and Neverthrow." },
      { text: "Applied Domain-Driven Design (DDD) principles with Zod for schema validation and domain consistency." },
      { text: "Built a multilingual platform using i18next and automated localization updates through a CI/CD pipeline." },
      { text: "Designed micro frontends with Module Federation, enabling runtime composition and team-level autonomy." }
    ]
  },
  {
    company: "JLSTANDRAD",
    role: "Frontend Developer",
    period: "12.2023 - 07.2024",
    link: "https://www.jlstandard.com/",
    description: [
      { text: "Developed in-app purchase and subscription features for both Google Play and Apple App Store platforms." },
      { text: "Integrated a web-based payment system using Bootpay to enable seamless online transactions." },
      { text: "Built internal back-office tools to support the operations team and streamline workflows." },
      { text: "Refactored the system to enable a strategic transition from a chatbot-focused service to an AI-powered memorial platform." }
    ]
  },
  {
    company: "PARD",
    role: "Manager of Developer",
    period: "11.2022 - 01.2024",
    link: "https://we-pard.com/",
    description: [
      { text: "Joined as the founding developer to lead overall program strategy and operational planning for PARD." },
      { text: "Developed and conducted technical curricula including seminars and assignments for iOS, Web, and Server tracks." },
      { text: "Led full-cycle development for major projects: PARD Official Website, PARD App, and Handong Univ. Student Council Website." },
      { text: "Established a scalable education system capable of graduating 50+ students per semester with industry-ready skills." }
    ]
  },
  {
    company: "Turning Point",
    role: "Frontend Developer",
    period: "04.2022 - 04.2023",
    description: [
      { text: "Refactored and implemented state management using Flutter Provider with the MVVM architecture pattern, improving scalability and maintainability." },
      { text: "Built robust user authentication and database infrastructure using Firebase Authentication for both Google and Apple sign-in." },
      { text: "Selected for ₩150M (~$170K AUD) in startup grants from K-Startup and Innopolis Campus programs." }
    ]
  }
];

// --- Components ---

const CustomCursor = () => {
  const [isHovering, setIsHovering] = useState(false);
  
  const cursorX = useSpring(0, { damping: 25, stiffness: 300 });
  const cursorY = useSpring(0, { damping: 25, stiffness: 300 });

  useEffect(() => {
    const moveCursor = (e: MouseEvent) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
    };

    const handleHover = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = !!(
        target.tagName === 'A' || 
        target.tagName === 'BUTTON' || 
        target.closest('button') || 
        target.closest('a') ||
        window.getComputedStyle(target).cursor === 'pointer'
      );
      
      setIsHovering(isInteractive);
    };

    window.addEventListener('mousemove', moveCursor);
    window.addEventListener('mouseover', handleHover);

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      window.removeEventListener('mouseover', handleHover);
    };
  }, [cursorX, cursorY]);

  return (
    <>
      <motion.div
        className="custom-cursor hidden lg:block"
        style={{
          left: cursorX,
          top: cursorY,
          scale: isHovering ? 2.5 : 1,
          backgroundColor: isHovering ? 'rgba(99, 91, 255, 0.1)' : 'rgba(99, 91, 255, 0.3)',
          border: isHovering ? '1px solid rgba(99, 91, 255, 0.2)' : 'none'
        }}
      />
      <motion.div
        className="custom-cursor-dot hidden lg:block"
        style={{
          left: cursorX,
          top: cursorY,
        }}
      />
    </>
  );
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const offset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  
  return null;
};

const Navbar = () => {
  const location = useLocation();

  const handleHomeClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const handleSectionClick = (sectionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 flex items-center justify-between px-8 py-6 backdrop-blur-md bg-white/70 border-b border-slate-200/50">
      <Link to="/" onClick={handleHomeClick} className="text-2xl font-bold tracking-tighter text-stripe-blue">
        benjie<span className="text-stripe-purple">.</span>
      </Link>
      <div className="hidden md:flex space-x-8 text-sm font-medium text-slate-600">
        <Link to="/" onClick={handleHomeClick} className="hover:text-stripe-purple transition-colors">Home</Link>
        <Link to="/projects" className="hover:text-stripe-purple transition-colors">Projects</Link>
        <a href="/#experience" onClick={(e) => handleSectionClick('experience', e)} className="hover:text-stripe-purple transition-colors">Experience</a>
        <a href="/#skills" onClick={(e) => handleSectionClick('skills', e)} className="hover:text-stripe-purple transition-colors">Stack</a>
      </div>
      <a href="/#contact" onClick={(e) => handleSectionClick('contact', e)} className="bg-stripe-purple text-white px-5 py-2 rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-stripe-purple/30 transition-all active:scale-95">
        Contact
      </a>
    </nav>
  );
};

const Hero = () => (
  <section className="relative pt-40 pb-20 px-8 overflow-hidden min-h-[90vh] flex items-center">
    <div className="mesh-gradient opacity-30 animate-pulse" />
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <motion.div 
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wide uppercase bg-stripe-purple/10 text-stripe-purple rounded-full"
        >
          Building for the future
        </motion.div>
        <h1 className="text-5xl sm:text-6xl md:text-8xl font-extrabold text-stripe-blue leading-tight mb-8 tracking-tight">
          Hello, I'm<br />
          <span className="text-transparent bg-clip-text bg-stripe-gradient">Benjie</span>
        </h1>
        <p className="text-xl text-slate-600 mb-10 leading-relaxed max-w-xl">
          I'm a developer passionate about building exceptional digital experiences. Specialized in modern web technologies and cross-platform solutions.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/projects" className="bg-stripe-blue text-white px-8 py-4 rounded-xl font-bold hover:bg-slate-800 transition-all flex items-center group shadow-xl shadow-stripe-blue/20">
            View Projects
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <a href="https://quddkflty.tistory.com" target="_blank" rel="noopener noreferrer" className="px-8 py-4 rounded-xl font-bold text-stripe-blue hover:bg-white/50 backdrop-blur-sm transition-all border border-slate-200 flex items-center">
            <BookOpen className="mr-2 w-5 h-5" />
            Visit Blog
          </a>
        </div>
      </motion.div>
      
      <motion.div 
        className="relative"
        initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1, delay: 0.3 }}
      >
        <div className="bg-white/80 backdrop-blur-xl p-6 sm:p-8 rounded-3xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] border border-white relative z-10">
          <div className="flex items-center space-x-2 mb-6">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <pre className="text-[11px] sm:text-sm font-mono text-slate-700 leading-relaxed overflow-x-auto custom-scrollbar">
            <code>{`const benjie = {
  stack: ['React', 'TypeScript', 'Next.js', 'Flutter', 'Django', 'Spring Boot'],
  email: 'ddkflty0@gmail.com',
  blog: 'quddkflty.tistory.com',
  status: 'Freelancing',
  principle: 'clarify the problem, then build for longevity'
};

async function createImpact() {
  return benjie.innovate({
    problem: 'clear',
    architecture: 'durable',
    reliability: 'consistent',
    focus: ['ux', 'craftsmanship'],
    execution: 'deliberate'
  });
}`}</code>
          </pre>
        </div>
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-stripe-purple/20 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-stripe-cyan/20 rounded-full blur-[100px] animate-pulse delay-1000" />
      </motion.div>
    </div>
  </section>
);

const ProjectModal = ({ project, onClose }: { project: any, onClose: () => void }) => {
  if (!project) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm bg-slate-900/40"
      onClick={onClose}
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-y-auto modal-scrollbar relative pr-1"
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="fixed md:absolute top-6 right-6 z-50 p-2 bg-white/80 backdrop-blur-md rounded-full shadow-lg hover:bg-white transition-colors"
        >
          <X className="w-6 h-6 text-slate-600" />
        </button>

        {/* Modal Content: Image followed by Details */}
        <div className="w-full">
          {/* Top Image Section */}
          <div className="w-full bg-white relative overflow-hidden flex items-center justify-center border-b border-slate-100">
            <img 
              src={project.image} 
              alt={project.title} 
              className="w-full h-auto object-cover"
            />
            {project.adminImage && (
              <div className="absolute bottom-6 right-6 w-2/5 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] rounded-[1.5rem] border-4 border-white overflow-hidden hidden md:block hover:scale-[1.02] transition-transform duration-500">
                <img src={project.adminImage} alt="Admin View" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-stripe-blue shadow-sm">
                  Admin Dashboard
                </div>
              </div>
            )}
          </div>

          {/* Bottom Info Section */}
          <div className="w-full p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
              {/* Left Side: Main Info */}
              <div className="md:col-span-7">
                <div className="mb-8">
                  <h2 className="text-4xl font-extrabold text-stripe-blue mb-2 tracking-tight">{project.title}</h2>
                  <p className="text-xl text-stripe-purple font-semibold mb-4">{project.subtitle}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tech.map((t: string) => (
                      <span key={t} className="text-[10px] font-bold tracking-wider uppercase text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md">
                        {t}
                      </span>
                    ))}
                  </div>
                <p className="text-slate-600 leading-relaxed text-lg mb-8">{project.longDescription}</p>
                
                <div className="flex flex-wrap gap-4">
                  {project.link && (
                    <a 
                      href={project.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-8 py-4 bg-stripe-blue text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-xl shadow-stripe-blue/20"
                    >
                      Visit Live Site <ExternalLink className="ml-2 w-5 h-5" />
                    </a>
                  )}
                  {project.youtubeLink && (
                    <a 
                      href={project.youtubeLink} 
          target="_blank"
          rel="noopener noreferrer"
                      className="inline-flex items-center px-8 py-4 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all shadow-xl shadow-red-600/20"
                    >
                      Watch Demo <Youtube className="ml-2 w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
              </div>

              {/* Right Side: Details */}
              <div className="md:col-span-5 space-y-8">
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Key Features</h4>
                  <ul className="space-y-3">
                    {project.details.features.map((f: string, i: number) => (
                      <li key={i} className="flex items-start text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-stripe-purple mr-3 shrink-0" />
                        <span className="text-sm font-medium">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Problem Solving</h4>
                  <ul className="space-y-3">
                    {project.details.solving.map((s: string, i: number) => (
                      <li key={i} className="flex items-start text-slate-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-stripe-cyan mt-2 mr-4 shrink-0" />
                        <span className="text-sm italic text-slate-600 leading-snug">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Impact & Result</h4>
                  <p className="text-stripe-blue text-sm font-bold leading-relaxed">{project.details.result}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ProjectCard = ({ project, index, onOpen }: { project: any, index: number, onOpen: (p: any) => void }) => (
  <motion.div 
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.7, delay: index * 0.1, ease: "circOut" }}
    className="group relative bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-[0_40px_80px_-15px_rgba(0,0,0,0.1)] transition-all duration-700 cursor-pointer"
    onClick={() => onOpen(project)}
  >
    <div className="aspect-[16/10] overflow-hidden relative bg-white flex items-center justify-center">
      <img 
        src={project.image} 
        alt={project.title} 
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-stripe-blue/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end p-8">
        <div className="flex space-x-4">
          <div className="p-3 bg-white/10 backdrop-blur-xl rounded-full text-white hover:bg-white/30 transition-all">
            <ExternalLink className="w-6 h-6" />
          </div>
        </div>
      </div>
    </div>
    <div className="p-8">
      <div className="flex flex-wrap gap-2 mb-4">
        {project.tech.slice(0, 3).map((t: string) => (
          <span key={t} className="text-[10px] font-bold tracking-wider uppercase text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md">
            {t}
          </span>
        ))}
        {project.tech.length > 3 && <span className="text-[10px] font-bold text-slate-400 px-1 py-1">+{project.tech.length - 3}</span>}
      </div>
      <h3 className="text-2xl font-bold text-stripe-blue mb-3 group-hover:text-stripe-purple transition-colors">
        {project.title}
      </h3>
      <p className="text-slate-600 leading-relaxed text-sm line-clamp-2">
        {project.description}
      </p>
    </div>
  </motion.div>
);

const WorkExperience = () => (
  <section id="experience" className="py-32 px-8 bg-white">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-20">
        <h2 className="text-4xl md:text-5xl font-bold text-stripe-blue mb-6">Work Experience</h2>
        <div className="w-20 h-1.5 bg-stripe-gradient mx-auto rounded-full" />
      </div>

      <div className="relative border-l-2 border-slate-100 ml-4 md:ml-0 pl-8 md:pl-12 space-y-20">
        {EXPERIENCES.map((exp, index) => (
          <motion.div 
            key={exp.company}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="relative"
          >
            {/* Timeline Dot */}
            <div className="absolute -left-[44px] md:-left-[60px] top-0 w-6 h-6 bg-white border-4 border-stripe-purple rounded-full z-10 shadow-lg shadow-stripe-purple/20" />
            
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between mb-4">
              <div className="flex items-center">
                <h3 className="text-2xl font-bold text-stripe-blue">{exp.company}</h3>
                {exp.link && (
                  <a href={exp.link} target="_blank" rel="noopener noreferrer" className="ml-3 text-stripe-purple hover:text-stripe-purple/80 transition-colors">
                    <ExternalLink className="w-5 h-5" />
                  </a>
                )}
              </div>
              <span className="text-sm font-bold text-stripe-purple bg-stripe-purple/10 px-3 py-1 rounded-full mt-2 md:mt-0">
                {exp.period}
              </span>
            </div>
            
            <div className="flex items-center text-slate-500 font-bold mb-6">
              <Briefcase className="w-4 h-4 mr-2" />
              {exp.role}
            </div>

            <ul className="space-y-4">
              {exp.description.map((bullet: any, i) => {
                const parts = bullet.text.split(':');
                const hasTitle = parts.length > 1;
                
                return (
                  <li key={i} className="flex items-start text-slate-600 leading-relaxed group">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2.5 mr-3 shrink-0" />
                    <span className="flex-1">
                      {hasTitle ? (
                        <>
                          <span className="font-bold text-stripe-blue">{parts[0]}</span>:
                          {parts.slice(1).join(':')}
                        </>
                      ) : (
                        bullet.text
                      )}
                      {bullet.link && (
                        <a 
                          href={bullet.link} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-stripe-purple/10 text-stripe-purple border border-stripe-purple/20 hover:bg-stripe-purple hover:text-white transition-all group/link"
                        >
                          LIVE <ExternalLink className="w-2.5 h-2.5 ml-1 group-hover/link:translate-x-0.5 transition-transform" />
                        </a>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => {
  const [selectedProject, setSelectedProject] = useState<any>(null);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Hero />
      
      <section className="py-32 px-8 bg-[#f6f9fc]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-bold text-stripe-blue mb-6">Featured Projects</h2>
              <p className="text-lg text-slate-600">Key innovative solutions built with a focus on problem-solving and user experience.</p>
            </div>
            <Link to="/projects" className="group flex items-center text-stripe-purple font-bold text-lg hover:translate-x-1 transition-all">
              View all projects
              <ChevronRight className="ml-1 w-6 h-6" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {PROJECTS.filter(p => p.featured).map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} onOpen={setSelectedProject} />
            ))}
          </div>
        </div>
      </section>

      <WorkExperience />
      <Skills />
      <ContactCTA />

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ProjectsPage = () => {
  const [selectedProject, setSelectedProject] = useState<any>(null);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="pt-40 pb-32 px-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-20 text-center">
          <h1 className="text-5xl md:text-7xl font-extrabold text-stripe-blue mb-8 tracking-tight">Project Archive</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            A comprehensive history of my development journey and the problems I've solved along the way.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} onOpen={setSelectedProject} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Skills = () => {
  const stack = [
    { name: "React", color: "20232A", logo: "react", logoColor: "61DAFB" },
    { name: "TypeScript", color: "3178C6", logo: "typescript", logoColor: "white" },
    { name: "Node.js", color: "339933", logo: "nodedotjs", logoColor: "white" },
    { name: "Next.js", color: "000000", logo: "nextdotjs", logoColor: "white" },
    { name: "AWS", color: "FF9900", logo: "amazonaws", logoColor: "white" },
    { name: "Dart", color: "0175C2", logo: "Dart", logoColor: "white" },
    { name: "Flutter", color: "02569B", logo: "Flutter", logoColor: "white" },
    { name: "Figma", color: "F24E1E", logo: "figma", logoColor: "white" },
    { name: "Firebase", color: "ffca28", logo: "firebase", logoColor: "black" },
    { name: "GitHub", color: "100000", logo: "github", logoColor: "white" }
  ];

  return (
    <section id="skills" className="py-32 px-8 bg-slate-50/50">
      <div className="max-w-7xl mx-auto text-center">
        <div className="mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-stripe-blue mb-6">Technical Stack</h2>
          <p className="text-slate-600 max-w-xl mx-auto">These are the core technologies I use to bring ideas to life.</p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-6">
          {stack.map((s, i) => (
            <motion.img 
              key={s.name}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              src={`https://img.shields.io/badge/${s.name}-${s.color}?style=for-the-badge&logo=${s.logo}&logoColor=${s.logoColor}`}
              alt={s.name}
              className="h-12 shadow-md hover:shadow-lg transition-shadow rounded-lg"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactCTA = () => (
  <section id="contact" className="py-40 px-8 relative overflow-hidden bg-white">
    <div className="max-w-7xl mx-auto relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="text-5xl md:text-7xl font-extrabold text-stripe-blue mb-8 tracking-tight leading-[1.1]">
            Ready to build <br />
            <span className="text-transparent bg-clip-text bg-stripe-gradient">something great?</span>
          </h2>
          <p className="text-xl text-slate-600 mb-12 leading-relaxed max-w-lg">
            Whether you have a specific project in mind or just want to explore possibilities, I'm here to help you turn your vision into reality.
          </p>
          <div className="flex flex-wrap gap-6">
            <a href="mailto:ddkflty0@gmail.com" className="bg-stripe-purple text-white px-10 py-5 rounded-2xl font-bold text-lg hover:shadow-2xl hover:shadow-stripe-purple/30 transition-all active:scale-95 flex items-center group">
              Get in touch
              <Send className="ml-3 w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a href="https://quddkflty.tistory.com" target="_blank" rel="noopener noreferrer" className="px-10 py-5 rounded-2xl font-bold text-lg text-stripe-blue hover:bg-slate-50 transition-all flex items-center group border border-slate-200">
              Read my blog
              <ChevronRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
        <div className="relative">
          <div className="bg-slate-50 rounded-[3rem] p-12 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-stripe-purple/10 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-stripe-purple/20 transition-all duration-1000" />
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center mb-8">
                <MessageSquare className="w-8 h-8 text-stripe-purple" />
              </div>
              <h3 className="text-2xl font-bold text-stripe-blue mb-4">Fast Communication</h3>
              <p className="text-slate-600 mb-8 leading-relaxed">
                I value clear and frequent communication. You can expect a response to your inquiries within 24 hours.
              </p>
              <div className="space-y-4">
                <div className="flex items-center text-slate-500 font-medium">
                  <div className="w-2 h-2 rounded-full bg-green-400 mr-3" />
                  Currently available for new projects
                </div>
                <div className="flex items-center text-slate-500 font-medium">
                  <div className="w-2 h-2 rounded-full bg-stripe-purple mr-3" />
                  Remote & Worldwide
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
  </section>
);

const Footer = () => (
  <footer className="py-24 px-8 bg-[#f6f9fc]">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-b border-slate-200 pb-20">
        <div className="md:col-span-4">
          <div className="text-3xl font-bold text-stripe-blue mb-6">
            benjie<span className="text-stripe-purple">.</span>
          </div>
          <p className="text-slate-500 max-w-xs leading-relaxed">
            Building seamless digital experiences with modern technology and a focus on long-term value.
          </p>
          <div className="flex space-x-5 mt-8">
            <a href="https://github.com/dasdfadssda" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-stripe-purple transition-colors">
              <Github className="w-6 h-6" />
            </a>
            <a href="https://quddkflty.tistory.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-stripe-purple transition-colors">
              <BookOpen className="w-6 h-6" />
            </a>
          </div>
        </div>
        
        <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-8">
          <div>
            <h4 className="font-bold text-stripe-blue mb-6 uppercase text-xs tracking-widest">Navigation</h4>
            <div className="flex flex-col space-y-4 text-slate-500 font-medium text-sm">
              <Link to="/" className="hover:text-stripe-purple transition-colors w-fit">Home</Link>
              <Link to="/projects" className="hover:text-stripe-purple transition-colors w-fit">Projects Archive</Link>
              <a href="/#experience" className="hover:text-stripe-purple transition-colors w-fit">Work Experience</a>
              <a href="/#skills" className="hover:text-stripe-purple transition-colors w-fit">Technical Stack</a>
            </div>
          </div>
          
          <div>
            <h4 className="font-bold text-stripe-blue mb-6 uppercase text-xs tracking-widest">Connect</h4>
            <div className="flex flex-col space-y-4 text-slate-500 font-medium text-sm">
              <a href="mailto:ddkflty0@gmail.com" className="hover:text-stripe-purple transition-colors w-fit">Email Me</a>
              <a href="https://github.com/dasdfadssda" target="_blank" rel="noopener noreferrer" className="hover:text-stripe-purple transition-colors w-fit">GitHub</a>
              <a href="https://quddkflty.tistory.com" target="_blank" rel="noopener noreferrer" className="hover:text-stripe-purple transition-colors w-fit">Tistory Blog</a>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-stripe-blue mb-6 uppercase text-xs tracking-widest">Location</h4>
            <div className="flex flex-col space-y-4 text-slate-500 font-medium text-sm">
              <span>Remote</span>
              <span>Seoul, South Korea</span>
              <span>Melbourne, Australia</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-12 flex flex-col md:flex-row justify-between items-center text-slate-400 text-xs font-medium">
        <span>© 2026 Benjie. Built with React & Tailwind.</span>
        <div className="flex space-x-6 mt-4 md:mt-0">
          <span>Define clearly, build to last.</span>
        </div>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <Router>
      <CustomCursor />
      <ScrollToTop />
      <div className="antialiased font-sans selection:bg-stripe-purple/20 selection:text-stripe-purple">
        <Navbar />
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/projects" element={<ProjectsPage />} />
          </Routes>
        </AnimatePresence>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
